import { Input } from "@/components/ui/input";
import { useState } from "react";
import { Button } from "../ui/button";
import { DialogHeader, DialogTitle } from "../ui/dialog";
import { useOnboarding } from "./onboarding-provider";
import { onboardingConfig } from "./onboading-config";
import { Label } from "../ui/label";
import { cn } from "@/lib/utils";

const OnboardingButton = ({
  isActive,
  children,
  onClick,
  className,
}: {
  isActive: boolean;
  children: React.ReactNode;
  onClick: () => void;
  className?: string;
}) => {
  const handleClick = () => {
    onClick();
  };

  return (
    <Button
      variant={"outline"}
      size="lg"
      onClick={handleClick}
      data-active={isActive ? "true" : "false"}
      className={cn(
        "text-left justify-start gap-3 rounded-lg h-auto px-4 py-2 shadow-none hover:bg-background group data-[active=true]:border-primary transition-all duration-300 ease-in-out whitespace-normal",
        className
      )}
    >
      <div className="rounded-full border size-5 flex items-center justify-center group-data-[active=true]:border-primary group-data-[active=true]:bg-primary transition-all duration-300 ease-in-out shrink-0">
        <div className="rounded-full size-2.5 group-data-[active=true]:bg-white transition-all duration-300 ease-in-out"></div>
      </div>

      <div className="flex-grow">{children}</div>
    </Button>
  );
};

const stepTransitionStyles = {
  enter: "opacity-0 ",
  enterActive: "opacity-100 translate-x-0 transition-all duration-300 ease-out",
  exit: "opacity-100 translate-x-0",
  exitActive: "opacity-0  transition-all duration-300 ease-in",
};

export function Step1() {
  const { onboardingData, updateOnboardingData, nextStep } = useOnboarding();

  const handleOptionSelect = (value: string) => {
    updateOnboardingData({ target_audience: value });
    // setTimeout(() => {
    //   nextStep();
    // }, 800);
  };

  return (
    <div
      className={`${stepTransitionStyles.enter} ${stepTransitionStyles.enterActive} h-full flex flex-col`}
    >
      <DialogHeader className="flex items-center justify-center mt-6">
        <DialogTitle className="text-center text-2xl font-semibold">
          {onboardingConfig.step1.question}
        </DialogTitle>
      </DialogHeader>

      <div className="flex flex-col gap-3 flex-grow mt-6">
        {onboardingConfig.step1.options.map((option) => (
          <OnboardingButton
            key={option.value}
            isActive={onboardingData.target_audience === option.value}
            onClick={() => handleOptionSelect(option.value)}
          >
            <div className="flex flex-col gap-0">
              <p className="text-base font-semibold">{option.label}</p>
              {option.subLabel && (
                <p className="text-sm block font-normal text-gray-500">
                  {option.subLabel}
                </p>
              )}
            </div>
          </OnboardingButton>
        ))}
      </div>
    </div>
  );
}

export function Step2() {
  const { onboardingData, updateOnboardingData, nextStep } = useOnboarding();

  const handleOptionSelect = (value: string) => {
    updateOnboardingData({
      employee_count: value,
    });
    // setTimeout(() => {
    //   nextStep();
    // }, 800);
  };

  return (
    <div
      className={`${stepTransitionStyles.enter} ${stepTransitionStyles.enterActive}`}
    >
      <DialogHeader className="flex items-center justify-center mt-6">
        <DialogTitle className="text-center text-2xl font-semibold">
          {onboardingConfig.step2.question}
        </DialogTitle>
      </DialogHeader>

      <div className="flex flex-col gap-3 md:mt-12 mt-6">
        {onboardingConfig.step2.options.map((option) => (
          <OnboardingButton
            key={option.value}
            isActive={onboardingData.employee_count === option.value}
            onClick={() => handleOptionSelect(option.value)}
          >
            {option.label}
          </OnboardingButton>
        ))}
      </div>
    </div>
  );
}

export function Step3() {
  const { onboardingData, updateOnboardingData, nextStep } = useOnboarding();
  const [showInputField, setShowInputField] = useState(false);

  const handleOptionSelect = (value: string) => {
    setShowInputField(false);
    updateOnboardingData({
      industry: value,
    });
    // setTimeout(() => {
    //   nextStep();
    // }, 800);
  };

  const handleOtherSelect = () => {
    setShowInputField(true);
    updateOnboardingData({
      industry: "",
    });
  };

  return (
    <div
      className={`${stepTransitionStyles.enter} ${stepTransitionStyles.enterActive}`}
    >
      <DialogHeader className="flex items-center justify-center mt-6">
        <DialogTitle className="text-center text-2xl font-semibold">
          {onboardingConfig.step3.question}
        </DialogTitle>
      </DialogHeader>

      <div className="grid grid-cols-2 gap-3 md:mt-9 mt-6">
        {onboardingConfig.step3.options.map((option) => (
          <OnboardingButton
            key={option.value}
            isActive={onboardingData.industry === option.value}
            onClick={() => handleOptionSelect(option.value)}
          >
            {option.label}
          </OnboardingButton>
        ))}

        <div className="w-full">
          {showInputField ? (
            <Input
              className="h-9 w-full"
              placeholder="Enter your industry..."
              onChange={(e) =>
                updateOnboardingData({ industry: e.target.value })
              }
            />
          ) : (
            <OnboardingButton
              isActive={onboardingData.industry === "other"}
              onClick={handleOtherSelect}
              className="w-full"
            >
              Other
            </OnboardingButton>
          )}
        </div>
      </div>
    </div>
  );
}

export function Step4() {
  const { onboardingData, updateOnboardingData, nextStep } = useOnboarding();

  const handleOptionSelect = (value: string) => {
    updateOnboardingData({
      previous_warmup_usage: value,
    });
    // setTimeout(() => {
    //   nextStep();
    // }, 800);
  };

  return (
    <div
      className={`${stepTransitionStyles.enter} ${stepTransitionStyles.enterActive}`}
    >
      <DialogHeader className="flex items-center justify-center mt-6">
        <DialogTitle className="text-center text-2xl font-semibold">
          {onboardingConfig.step4.question}
        </DialogTitle>
      </DialogHeader>

      <div className="flex flex-col gap-3 md:mt-12 mt-6">
        {onboardingConfig.step4.options.map((option) => (
          <OnboardingButton
            key={option.value}
            isActive={onboardingData.previous_warmup_usage === option.value}
            onClick={() => handleOptionSelect(option.value)}
          >
            {option.label}
          </OnboardingButton>
        ))}
      </div>
    </div>
  );
}

export function Step5() {
  const { onboardingData, updateOnboardingData, nextStep } = useOnboarding();

  const handleOptionSelect = (value: string) => {
    updateOnboardingData({
      mailbox_count: value,
    });
    // setTimeout(() => {
    //   nextStep();
    // }, 800);
  };

  return (
    <div
      className={`${stepTransitionStyles.enter} ${stepTransitionStyles.enterActive}`}
    >
      <DialogHeader className="flex items-center justify-center mt-6">
        <DialogTitle className="text-center text-2xl font-semibold">
          {onboardingConfig.step5.question}
        </DialogTitle>
      </DialogHeader>

      <div className="grid grid-cols-2 gap-3 md:mt-12 mt-6">
        {onboardingConfig.step5.options.map((option) => (
          <OnboardingButton
            key={option.value}
            isActive={onboardingData.mailbox_count === option.value}
            onClick={() => handleOptionSelect(option.value)}
          >
            {option.label}
          </OnboardingButton>
        ))}
      </div>
    </div>
  );
}

export function Step6() {
  const { onboardingData, updateOnboardingData, nextStep } = useOnboarding();

  const handleOptionSelect = (value: string) => {
    updateOnboardingData({
      monthly_email_volume: value,
    });
    // setTimeout(() => {
    //   nextStep();
    // }, 800);
  };

  return (
    <div
      className={`${stepTransitionStyles.enter} ${stepTransitionStyles.enterActive}`}
    >
      <DialogHeader className="flex items-center justify-center mt-6">
        <DialogTitle className="text-center text-2xl font-semibold">
          {onboardingConfig.step6.question}
        </DialogTitle>
      </DialogHeader>

      <div className="flex flex-col gap-3 md:mt-5 mt-6">
        {onboardingConfig.step6.options.map((option) => (
          <OnboardingButton
            key={option.value}
            isActive={onboardingData.monthly_email_volume === option.value}
            onClick={() => handleOptionSelect(option.value)}
          >
            {option.label}
          </OnboardingButton>
        ))}
      </div>
    </div>
  );
}

export function Step7() {
  const { onboardingData, updateOnboardingData, nextStep } = useOnboarding();
  const [showInputField, setShowInputField] = useState(false);

  const handleOptionSelect = (value: string) => {
    setShowInputField(false);
    updateOnboardingData({
      referral_source: value,
    });
    // setTimeout(() => {
    //   nextStep();
    // }, 800);
  };

  const handleOtherSelect = () => {
    setShowInputField(true);
    updateOnboardingData({
      referral_source: "",
    });
  };

  return (
    <div
      className={`${stepTransitionStyles.enter} ${stepTransitionStyles.enterActive}`}
    >
      <DialogHeader className="flex items-center justify-center mt-6">
        <DialogTitle className="text-center text-2xl font-semibold">
          {onboardingConfig.step7.question}
        </DialogTitle>
      </DialogHeader>

      <div className="flex flex-col gap-3 md:mt-6 mt-5">
        {onboardingConfig.step7.options.map((option) => (
          <OnboardingButton
            key={option.value}
            isActive={onboardingData.referral_source === option.value}
            onClick={() => handleOptionSelect(option.value)}
          >
            {option.label}
          </OnboardingButton>
        ))}

        <div className="w-full">
          {showInputField ? (
            <Input
              className="h-9 w-full rounded-lg"
              placeholder="Tell us how you found us..."
              onChange={(e) =>
                updateOnboardingData({ referral_source: e.target.value })
              }
            />
          ) : (
            <OnboardingButton
              isActive={onboardingData.referral_source === "other"}
              onClick={handleOtherSelect}
              className="w-full"
            >
              Other
            </OnboardingButton>
          )}
        </div>
      </div>
    </div>
  );
}
